import { useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Upload, Star, Trash2, Download, RotateCcw, FileText, Image as ImageIcon, Film, Music, FileArchive, File as FileIcon, Search, History, Folder, FolderPlus, ChevronRight, Home, Pencil } from "lucide-react";
import { api, API, apiError } from "@/lib/api";
import { timeAgo } from "@/lib/constants";
import { SpaceSection } from "@/components/SpaceSection";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";

export const fmtSize = (b) => {
  if (b < 1024) return `${b} B`;
  if (b < 1048576) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1048576).toFixed(1)} MB`;
};

const CAT_ICONS = { image: ImageIcon, pdf: FileText, doc: FileText, video: Film, audio: Music, other: FileArchive };

export const CategoryIcon = ({ category, className = "h-4 w-4" }) => {
  const Icon = CAT_ICONS[category] || FileIcon;
  return <Icon className={className} />;
};

export const downloadUrl = (fileId) => `${API}/files/${fileId}/download`;

const FilePreview = ({ file }) => {
  const url = downloadUrl(file.id);
  if (file.category === "image") return <img src={url} alt={file.name} className="max-h-72 w-full object-contain rounded-lg bg-secondary" />;
  if (file.category === "pdf") return <iframe src={url} title={file.name} className="w-full h-72 rounded-lg border" />;
  if (file.category === "video") return <video src={url} controls className="w-full max-h-72 rounded-lg" />;
  if (file.category === "audio") return <audio src={url} controls className="w-full" />;
  if (file.mime_type?.startsWith("text/")) return <iframe src={url} title={file.name} className="w-full h-56 rounded-lg border bg-white" />;
  return (
    <div className="p-8 text-center bg-secondary rounded-lg">
      <FileArchive className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
      <p className="text-xs text-muted-foreground">Vista previa no disponible. Descarga el archivo para abrirlo.</p>
    </div>
  );
};

export default function FilesPage() {
  const { id: workspaceId } = useParams();
  const qc = useQueryClient();
  const inputRef = useRef(null);
  const replaceRef = useRef(null);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("all");
  const [showTrash, setShowTrash] = useState(false);
  const [showFavs, setShowFavs] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [folderId, setFolderId] = useState(null);

  const { data: folders = [] } = useQuery({
    queryKey: ["folders", workspaceId],
    queryFn: async () => (await api.get(`/workspaces/${workspaceId}/folders`)).data,
  });
  const browsing = !q && !showTrash && !showFavs;
  const currentFolders = folders.filter((f) => (f.parent_id || null) === folderId);
  const breadcrumb = [];
  let cur = folders.find((f) => f.id === folderId);
  while (cur) { breadcrumb.unshift(cur); cur = folders.find((f) => f.id === cur.parent_id); }

  const { data: files = [] } = useQuery({
    queryKey: ["files", workspaceId, q, category, showTrash, showFavs, folderId],
    queryFn: async () => {
      const params = { deleted: showTrash };
      if (!q && !showTrash && !showFavs) params.folder = folderId || "root";
      if (q) params.q = q;
      if (category !== "all") params.category = category;
      if (showFavs) params.favorites = true;
      return (await api.get(`/workspaces/${workspaceId}/files`, { params })).data;
    },
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ["files", workspaceId] });
  const selected = files.find((f) => f.id === selectedId);

  const upload = async (fileList) => {
    setUploading(true);
    for (const f of fileList) {
      const form = new FormData();
      form.append("file", f);
      if (folderId) form.append("folder_id", folderId);
      try {
        await api.post(`/workspaces/${workspaceId}/files`, form);
      } catch (e) {
        toast.error(`${f.name}: ${apiError(e)}`);
      }
    }
    setUploading(false);
    invalidate();
    toast.success(fileList.length > 1 ? `${fileList.length} archivos subidos` : "Archivo subido");
  };

  const act = async (fn, msg) => {
    try {
      await fn();
      invalidate();
      if (msg) toast.success(msg);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const invalidateFolders = () => qc.invalidateQueries({ queryKey: ["folders", workspaceId] });

  const createFolder = async () => {
    const name = window.prompt("Nombre de la carpeta:");
    if (!name || !name.trim()) return;
    try {
      await api.post(`/workspaces/${workspaceId}/folders`, { name: name.trim(), parent_id: folderId });
      invalidateFolders();
      toast.success("Carpeta creada");
    } catch (e) { toast.error(apiError(e)); }
  };

  const renameFolder = async (folder) => {
    const name = window.prompt("Nuevo nombre:", folder.name);
    if (!name || !name.trim()) return;
    try { await api.put(`/folders/${folder.id}`, { name: name.trim() }); invalidateFolders(); } catch (e) { toast.error(apiError(e)); }
  };

  const deleteFolder = async (folder) => {
    if (!window.confirm(`¿Eliminar la carpeta "${folder.name}"? Su contenido se moverá al nivel superior.`)) return;
    try { await api.delete(`/folders/${folder.id}`); invalidateFolders(); invalidate(); toast.success("Carpeta eliminada"); } catch (e) { toast.error(apiError(e)); }
  };

  const rename = async (file) => {
    const name = window.prompt("Nuevo nombre:", file.name);
    if (name && name.trim()) act(() => api.put(`/files/${file.id}`, { name: name.trim() }), "Renombrado");
  };

  return (
    <SpaceSection title="Biblioteca" actions={
      <div className="flex gap-2">
        <Button size="sm" variant="outline" data-testid="files-new-folder-btn" onClick={createFolder}>
          <FolderPlus className="h-4 w-4 mr-1" /> Carpeta
        </Button>
        <Button size="sm" data-testid="files-upload-btn" onClick={() => inputRef.current?.click()} disabled={uploading}>
          <Upload className="h-4 w-4 mr-1" /> {uploading ? "Subiendo..." : "Subir"}
        </Button>
      </div>
    }>
      <input ref={inputRef} type="file" multiple hidden data-testid="files-upload-input"
        onChange={(e) => { if (e.target.files.length) upload([...e.target.files]); e.target.value = ""; }} />

      <div className="flex flex-wrap items-center gap-2" data-testid="files-filter-bar">
        <div className="relative">
          <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="files-search-input" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar archivos..." className="pl-8 h-8 w-48 text-sm" />
        </div>
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger className="h-8 w-36 text-xs" data-testid="files-category-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los tipos</SelectItem>
            <SelectItem value="image">Imágenes</SelectItem>
            <SelectItem value="pdf">PDF</SelectItem>
            <SelectItem value="doc">Documentos</SelectItem>
            <SelectItem value="video">Vídeo</SelectItem>
            <SelectItem value="audio">Audio</SelectItem>
            <SelectItem value="other">Otros</SelectItem>
          </SelectContent>
        </Select>
        <Button variant={showFavs ? "default" : "outline"} size="sm" className="h-8 text-xs"
          data-testid="files-favorites-toggle" onClick={() => setShowFavs(!showFavs)}>
          <Star className="h-3.5 w-3.5 mr-1" /> Favoritos
        </Button>
        <Button variant={showTrash ? "destructive" : "outline"} size="sm" className="h-8 text-xs"
          data-testid="files-trash-toggle" onClick={() => setShowTrash(!showTrash)}>
          <Trash2 className="h-3.5 w-3.5 mr-1" /> Papelera
        </Button>
      </div>

      {browsing && (
        <div className="flex items-center gap-1 text-sm flex-wrap" data-testid="files-breadcrumb">
          <button onClick={() => setFolderId(null)} data-testid="files-breadcrumb-root"
            className={`flex items-center gap-1 px-2 py-1 rounded-lg hover:bg-secondary ${!folderId ? "font-semibold" : "text-muted-foreground"}`}>
            <Home className="h-3.5 w-3.5" /> Biblioteca
          </button>
          {breadcrumb.map((b) => (
            <span key={b.id} className="flex items-center gap-1">
              <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
              <button onClick={() => setFolderId(b.id)}
                className={`px-2 py-1 rounded-lg hover:bg-secondary ${folderId === b.id ? "font-semibold" : "text-muted-foreground"}`}>
                {b.name}
              </button>
            </span>
          ))}
        </div>
      )}

      {browsing && currentFolders.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2" data-testid="files-folders-grid">
          {currentFolders.map((f) => (
            <div key={f.id} data-testid={`folder-card-${f.id}`}
              className="group flex items-center gap-2 p-3 rounded-xl bg-card shadow-sm hover:shadow-md cursor-pointer transition-shadow"
              onClick={() => setFolderId(f.id)}>
              <Folder className="h-5 w-5 text-amber-500 shrink-0" fill="#F9AB0033" />
              <span className="text-sm font-medium truncate flex-1">{f.name}</span>
              <button className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-foreground"
                data-testid={`folder-rename-${f.id}`}
                onClick={(e) => { e.stopPropagation(); renameFolder(f); }}>
                <Pencil className="h-3.5 w-3.5" />
              </button>
              <button className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive"
                data-testid={`folder-delete-${f.id}`}
                onClick={(e) => { e.stopPropagation(); deleteFolder(f); }}>
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}

      {files.length === 0 ? (
        <Card className="p-12 text-center border-dashed">
          <Upload className="h-8 w-8 mx-auto text-muted-foreground mb-3" />
          <p className="text-sm text-muted-foreground">
            {showTrash ? "La papelera está vacía." : "Sube tu primer archivo. Todo queda unido a su contexto."}
          </p>
        </Card>
      ) : (
        <Card className="divide-y divide-border" data-testid="files-list">
          {files.map((f) => (
            <div key={f.id} className="p-3 flex items-center gap-3 hover:bg-secondary/50 cursor-pointer group"
              onClick={() => setSelectedId(f.id)} data-testid={`file-row-${f.id}`}>
              {f.category === "image" ? (
                <img src={downloadUrl(f.id)} alt="" className="h-10 w-10 rounded-lg object-cover shrink-0" loading="lazy" />
              ) : (
                <div className="h-10 w-10 rounded-lg bg-secondary flex items-center justify-center shrink-0 text-muted-foreground">
                  <CategoryIcon category={f.category} />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{f.name}</p>
                <p className="text-xs text-muted-foreground">
                  {fmtSize(f.size)} · {f.uploaded_by_name} · {timeAgo(f.created_at)}
                  {f.version > 1 && <span> · v{f.version}</span>}
                </p>
              </div>
              {!showTrash && (
                <button onClick={(e) => { e.stopPropagation(); act(() => api.post(`/files/${f.id}/favorite`)); }}
                  data-testid={`file-favorite-btn-${f.id}`} className="shrink-0">
                  <Star className={`h-4 w-4 ${f.favorited_by?.length ? "" : "opacity-0 group-hover:opacity-100"} transition-opacity`}
                    fill={f.favorited_by?.length ? "#F9AB00" : "none"} stroke={f.favorited_by?.length ? "#F9AB00" : "currentColor"} />
                </button>
              )}
              {showTrash ? (
                <div className="flex gap-1 shrink-0">
                  <Button variant="ghost" size="sm" className="h-8 text-xs" data-testid={`file-restore-btn-${f.id}`}
                    onClick={(e) => { e.stopPropagation(); act(() => api.post(`/files/${f.id}/restore`), "Restaurado"); }}>
                    <RotateCcw className="h-3.5 w-3.5 mr-1" /> Restaurar
                  </Button>
                  <Button variant="ghost" size="sm" className="h-8 text-xs text-destructive" data-testid={`file-permanent-delete-btn-${f.id}`}
                    onClick={(e) => { e.stopPropagation(); act(() => api.delete(`/files/${f.id}/permanent`), "Eliminado definitivamente"); }}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              ) : (
                <a href={downloadUrl(f.id)} download={f.name} onClick={(e) => e.stopPropagation()}
                  data-testid={`file-download-btn-${f.id}`}
                  className="shrink-0 opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-foreground">
                  <Download className="h-4 w-4" />
                </a>
              )}
            </div>
          ))}
        </Card>
      )}

      <Sheet open={!!selected} onOpenChange={(o) => !o && setSelectedId(null)}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto" data-testid="file-detail-sheet">
          {selected && (
            <>
              <SheetHeader>
                <SheetTitle className="font-heading text-lg break-all">{selected.name}</SheetTitle>
                <SheetDescription>
                  {fmtSize(selected.size)} · subido por {selected.uploaded_by_name} · {timeAgo(selected.created_at)}
                </SheetDescription>
              </SheetHeader>
              <div className="space-y-5 mt-4">
                <FilePreview file={selected} />
                <div className="flex flex-wrap gap-2">
                  <Button asChild size="sm" variant="outline" data-testid="file-detail-download-btn">
                    <a href={downloadUrl(selected.id)} download={selected.name}>
                      <Download className="h-3.5 w-3.5 mr-1" /> Descargar
                    </a>
                  </Button>
                  <Button size="sm" variant="outline" data-testid="file-detail-rename-btn" onClick={() => rename(selected)}>
                    Renombrar
                  </Button>
                  <Select value={selected.folder_id || "root"} onValueChange={(v) =>
                    act(() => api.post(`/files/${selected.id}/move`, { folder_id: v === "root" ? null : v }), "Archivo movido")}>
                    <SelectTrigger className="h-8 w-40 text-xs" data-testid="file-move-select">
                      <SelectValue placeholder="Mover a..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="root">📁 Biblioteca (raíz)</SelectItem>
                      {folders.map((f) => <SelectItem key={f.id} value={f.id}>📁 {f.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Button size="sm" variant="outline" data-testid="file-detail-replace-btn" onClick={() => replaceRef.current?.click()}>
                    <History className="h-3.5 w-3.5 mr-1" /> Nueva versión
                  </Button>
                  <input ref={replaceRef} type="file" hidden onChange={async (e) => {
                    const f = e.target.files[0];
                    if (!f) return;
                    const form = new FormData();
                    form.append("file", f);
                    act(() => api.post(`/files/${selected.id}/replace`, form), "Nueva versión subida");
                    e.target.value = "";
                  }} />
                  {!selected.deleted_at && (
                    <Button size="sm" variant="ghost" className="text-destructive" data-testid="file-detail-trash-btn"
                      onClick={() => { act(() => api.delete(`/files/${selected.id}`), "Enviado a la papelera"); setSelectedId(null); }}>
                      <Trash2 className="h-3.5 w-3.5 mr-1" /> Papelera
                    </Button>
                  )}
                </div>
                <div className="space-y-2">
                  <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">
                    Versiones <Badge variant="secondary" className="ml-1">v{selected.version}</Badge>
                  </p>
                  {(selected.versions || []).length === 0 ? (
                    <p className="text-xs text-muted-foreground">Sin versiones anteriores.</p>
                  ) : (
                    [...selected.versions].reverse().map((v) => (
                      <div key={v.version} className="flex items-center gap-2 text-xs border rounded-lg px-3 py-2" data-testid={`file-version-row-${v.version}`}>
                        <Badge variant="outline">v{v.version}</Badge>
                        <span className="flex-1 text-muted-foreground">{fmtSize(v.size)} · {v.uploaded_by_name}</span>
                        <Button variant="ghost" size="sm" className="h-6 text-xs" data-testid={`file-version-restore-${v.version}`}
                          onClick={() => act(() => api.post(`/files/${selected.id}/versions/${v.version}/restore`), `Versión ${v.version} restaurada`)}>
                          Restaurar
                        </Button>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </SpaceSection>
  );
}
